import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ExternalLink, Info, Star } from "lucide-react";
import type { Product } from "@/data/products";
import { trackOfferClick } from "@/lib/analytics";

const fmt = (n: number) =>
  n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

export function ProductDetail({
  product,
  onClose,
}: {
  product: Product | null;
  onClose: () => void;
}) {
  const hasDiscount = Boolean(
    product && product.desconto > 0 && product.precoOriginal > product.precoAtual,
  );

  return (
    <Dialog open={!!product} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-3xl overflow-hidden p-0">
        {product ? (
          <div className="grid gap-0 md:grid-cols-2">
            <div className="aspect-square bg-muted">
              <img
                src={product.imagem}
                alt={product.nome}
                className="h-full w-full object-cover"
              />
            </div>
            <div className="flex flex-col gap-3 p-6">
              <DialogHeader className="space-y-1 text-left">
                <span className="text-xs font-semibold uppercase tracking-wider text-primary">
                  {product.categoria} · {product.loja}
                </span>
                <DialogTitle className="font-display text-2xl leading-tight">
                  {product.nome}
                </DialogTitle>
              </DialogHeader>

              <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                <Star className="h-4 w-4 fill-[var(--star)] text-[var(--star)]" />
                <span className="font-semibold text-foreground">{product.avaliacao}</span>
                <span>· {product.quantidadeAvaliacoes} avaliações</span>
              </div>

              <div className="text-sm font-medium text-foreground/75">
                {product.vendidos > 0
                  ? `${product.vendidos.toLocaleString("pt-BR")} vendas realizadas`
                  : "Produto novo no catálogo"}
              </div>

              <div className="flex flex-wrap items-end gap-3 pt-2">
                <span
                  className={`font-display text-4xl ${hasDiscount ? "text-primary" : "text-foreground"}`}
                >
                  {fmt(product.precoAtual)}
                </span>
                {hasDiscount ? (
                  <>
                    <span className="pb-1 text-sm text-muted-foreground line-through">
                      {fmt(product.precoOriginal)}
                    </span>
                    <span className="ml-auto rounded-full bg-[var(--sale)] px-2.5 py-0.5 text-xs font-semibold text-white">
                      -{product.desconto}%
                    </span>
                  </>
                ) : null}
              </div>

              <p className="text-sm text-muted-foreground">
                Achadinho selecionado pela nossa curadoria. Disponível enquanto durarem os
                estoques na loja parceira {product.loja}.
              </p>

              <button
                onClick={() => {
                  trackOfferClick(product, "detail");
                  window.open(product.linkAfiliado, "_blank", "noopener,noreferrer");
                }}
                className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-[image:var(--gradient-primary)] px-6 py-3.5 font-medium text-primary-foreground shadow-[var(--shadow-soft)] transition hover:opacity-95"
              >
                Ir para a loja <ExternalLink className="h-4 w-4" />
              </button>

              <p className="flex items-start gap-1.5 text-[11px] leading-relaxed text-muted-foreground">
                <Info className="mt-0.5 h-3 w-3 shrink-0" />
                Você será redirecionado para a loja parceira. Alguns links podem ser de
                afiliado e podemos receber uma comissão sem custo adicional para você.
              </p>
            </div>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
